export interface Canton {
  id: string
  name: string
  fullName: string
  description: string
  restaurantCount: number
}

export interface Category {
  id: string
  name: string
  icon: string
  count: number
}

export interface MenuItem {
  name: string
  description: string
  price: string
  image?: string
}

export interface Review {
  author: string
  rating: number
  text: string
  date: string
}

export interface Restaurant {
  id: string
  name: string
  categoryId: string
  cantonId: string
  description: string
  image: string
  rating: number
  reviewCount: number
  priceRange: '$' | '$$' | '$$$'
  address: string
  featured: boolean
  tags: string[]
  menuItems: MenuItem[]
  reviews: Review[]
  coordinates: { lat: number; lng: number }
}

export const cantones: Canton[] = [
  {
    id: 'tarrazu',
    name: 'Tarrazú',
    fullName: 'San Marcos de Tarrazú',
    description:
      'Capital del mejor café del mundo. Montañas, cultura cafetalera y gastronomía de altura.',
    restaurantCount: 8,
  },
  {
    id: 'dota',
    name: 'Dota',
    fullName: 'Santa María de Dota',
    description:
      'Truchas frescas, bosque nuboso y la autenticidad de la montaña.',
    restaurantCount: 6,
  },
  {
    id: 'leon-cortes',
    name: 'León Cortés',
    fullName: 'San Pablo de León Cortés',
    description: 'Tradición pura. Comida casera, campo y raíces santeñas.',
    restaurantCount: 5,
  },
]

export const categories: Category[] = [
  { id: 'restaurantes', name: 'Restaurantes', icon: 'UtensilsIcon', count: 8 },
  { id: 'sodas', name: 'Sodas', icon: 'StoreIcon', count: 5 },
  { id: 'cafeterias', name: 'Cafeterías', icon: 'CoffeeIcon', count: 4 },
  { id: 'ventanitas', name: 'Ventanitas', icon: 'DoorOpenIcon', count: 2 },
  { id: 'bares', name: 'Bares', icon: 'WineIcon', count: 2 },
]

const IMAGES = [
  'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&q=80',
  'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800&q=80',
  'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&q=80',
  'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=800&q=80',
  'https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=800&q=80',
  'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=800&q=80',
  'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80',
  'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800&q=80',
  'https://images.unsplash.com/photo-1476224203421-9ac39bcb3327?w=800&q=80',
  'https://images.unsplash.com/photo-1551218808-94e220e084d2?w=800&q=80',
  'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=800&q=80',
  'https://images.unsplash.com/photo-1600891964092-4316c288032e?w=800&q=80',
]

export const restaurants: Restaurant[] = [
  // TARRAZÚ
  {
    id: '1',
    name: 'El Mirador de Tarrazú',
    categoryId: 'restaurantes',
    cantonId: 'tarrazu',
    description:
      'Alta cocina con ingredientes locales y la mejor vista de los cafetales del valle.',
    image: IMAGES[6],
    rating: 4.9,
    reviewCount: 124,
    priceRange: '$$$',
    address: 'Camino a Llano Bonito, 2km del centro, San Marcos',
    featured: true,
    tags: ['Vista al valle', 'Gourmet', 'Café Tarrazú'],
    menuItems: [
      {
        name: 'Lomito al Café',
        description: 'Corte premium bañado en reducción de café de la zona',
        price: '₡14,500',
      },
      {
        name: 'Trucha a las Finas Hierbas',
        description: 'Trucha fresca de la zona con vegetales orgánicos',
        price: '₡9,500',
      },
      {
        name: 'Cheesecake de Mora',
        description: 'Con moras frescas de altura',
        price: '₡4,000',
      },
    ],
    reviews: [
      {
        author: 'Diego M.',
        rating: 5,
        text: 'La vista al atardecer es increíble y la comida espectacular.',
        date: 'Hace 1 semana',
      },
      {
        author: 'Laura S.',
        rating: 5,
        text: 'El lomito en salsa de café es un must.',
        date: 'Hace 3 semanas',
      },
    ],
    coordinates: { lat: 9.66, lng: -84.02 },
  },
  {
    id: '2',
    name: 'Soda Doña Flor',
    categoryId: 'sodas',
    cantonId: 'tarrazu',
    description:
      'El sabor de la abuela. Casados gigantes y el mejor gallo pinto de San Marcos.',
    image: IMAGES[10],
    rating: 4.7,
    reviewCount: 312,
    priceRange: '$',
    address: '100m sur del parque central, San Marcos',
    featured: false,
    tags: ['Comida Típica', 'Casados', 'Tradicional'],
    menuItems: [
      {
        name: 'Casado con Chuleta',
        description: 'Arroz, frijoles, picadillo, plátano, ensalada y chuleta',
        price: '₡3,500',
      },
      {
        name: 'Gallo Pinto con Huevo',
        description: 'El desayuno de los campeones',
        price: '₡2,500',
      },
      {
        name: 'Olla de Carne',
        description: 'Solo fines de semana, con verduras de la zona',
        price: '₡4,500',
      },
    ],
    reviews: [
      {
        author: 'Carlos V.',
        rating: 5,
        text: 'Como comer en casa. Doña Flor siempre te atiende con una sonrisa.',
        date: 'Hace 2 días',
      },
      {
        author: 'Ana P.',
        rating: 4,
        text: 'Las porciones son enormes, excelente precio.',
        date: 'Hace 1 mes',
      },
    ],
    coordinates: { lat: 9.658, lng: -84.018 },
  },
  {
    id: '3',
    name: 'Café de Altura',
    categoryId: 'cafeterias',
    cantonId: 'tarrazu',
    description:
      'Cafetería de especialidad. Disfrutá del mejor café del mundo en su lugar de origen.',
    image: IMAGES[9],
    rating: 4.9,
    reviewCount: 245,
    priceRange: '$$',
    address: 'Frente a la iglesia católica, San Marcos',
    featured: true,
    tags: ['Café Tarrazú', 'Postres', 'Especialidad'],
    menuItems: [
      {
        name: 'V60 Geisha',
        description: 'Método de filtrado, notas florales y a jazmín',
        price: '₡3,500',
      },
      {
        name: 'Cappuccino',
        description: 'Espresso perfecto con leche texturizada',
        price: '₡2,200',
      },
      {
        name: 'Torta Chilena',
        description: 'Receta casera, mucho dulce de leche',
        price: '₡3,000',
      },
    ],
    reviews: [
      {
        author: 'María J.',
        rating: 5,
        text: 'El mejor café que he probado. Tienen métodos de extracción increíbles.',
        date: 'Hace 5 días',
      },
    ],
    coordinates: { lat: 9.661, lng: -84.019 },
  },
  {
    id: '4',
    name: 'La Parrilla del Santos',
    categoryId: 'restaurantes',
    cantonId: 'tarrazu',
    description:
      'Cortes a la parrilla, hamburguesas artesanales y ambiente relajado.',
    image: IMAGES[1],
    rating: 4.6,
    reviewCount: 189,
    priceRange: '$$',
    address: 'Costado este del estadio municipal, San Marcos',
    featured: true,
    tags: ['Parrilla', 'Hamburguesas', 'Carnes'],
    menuItems: [
      {
        name: 'Hamburguesa La Santeña',
        description:
          'Torta de res 200g, queso artesanal, tocineta, aros de cebolla',
        price: '₡5,500',
      },
      {
        name: 'Costillas BBQ',
        description: 'Medio costillar ahumado por 6 horas',
        price: '₡8,500',
      },
      {
        name: 'Choripán',
        description: 'Chorizo artesanal en pan ciabatta con chimichurri',
        price: '₡3,500',
      },
    ],
    reviews: [
      {
        author: 'Esteban R.',
        rating: 5,
        text: 'Las mejores hamburguesas de la zona, sin duda.',
        date: 'Hace 2 semanas',
      },
    ],
    coordinates: { lat: 9.655, lng: -84.015 },
  },
  {
    id: '5',
    name: 'Bar El Rincón',
    categoryId: 'bares',
    cantonId: 'tarrazu',
    description:
      'El bar del pueblo. Bocas tradicionales, cervezas frías y buen ambiente.',
    image: IMAGES[11],
    rating: 4.3,
    reviewCount: 95,
    priceRange: '$',
    address: 'Barrio La Sabana, San Marcos',
    featured: false,
    tags: ['Bocas', 'Cerveza', 'Nocturno'],
    menuItems: [
      {
        name: 'Chifrijo',
        description: 'El clásico tico, bien cargado de chicharrón',
        price: '₡4,000',
      },
      {
        name: 'Ceviche de Plátano',
        description: 'Boca tradicional de la casa',
        price: '₡2,000',
      },
      {
        name: 'Cerveza Nacional',
        description: 'Imperial o Pilsen bien fría',
        price: '₡1,500',
      },
    ],
    reviews: [
      {
        author: 'Luis G.',
        rating: 4,
        text: 'Buen ambiente los viernes, las bocas son muy ricas.',
        date: 'Hace 1 mes',
      },
    ],
    coordinates: { lat: 9.652, lng: -84.02 },
  },
  {
    id: '6',
    name: 'Ventanita La Esquina',
    categoryId: 'ventanitas',
    cantonId: 'tarrazu',
    description: 'Comida rápida para llevar. Empanadas, tacos y papas.',
    image: IMAGES[3],
    rating: 4.5,
    reviewCount: 150,
    priceRange: '$',
    address: 'Frente al liceo, San Marcos',
    featured: false,
    tags: ['Rápido', 'Empanadas', 'Para llevar'],
    menuItems: [
      {
        name: 'Empanada Arreglada',
        description: 'Empanada de carne con repollo, mayonesa y salsa',
        price: '₡1,500',
      },
      {
        name: 'Taco Tico',
        description: 'Taco frito de carne con repollo',
        price: '₡1,200',
      },
    ],
    reviews: [
      {
        author: 'Sofía C.',
        rating: 5,
        text: 'Las empanadas arregladas son mi salvación después del colegio.',
        date: 'Hace 3 días',
      },
    ],
    coordinates: { lat: 9.663, lng: -84.017 },
  },
  {
    id: '7',
    name: 'Restaurante Los Cerros',
    categoryId: 'restaurantes',
    cantonId: 'tarrazu',
    description: 'Comida típica en un ambiente familiar y acogedor.',
    image: IMAGES[8],
    rating: 4.4,
    reviewCount: 110,
    priceRange: '$$',
    address: 'Entrada a San Marcos, Tarrazú',
    featured: false,
    tags: ['Familiar', 'Comida Típica', 'Sopas'],
    menuItems: [
      {
        name: 'Sopa Negra',
        description: 'Con huevo duro y tortillas palmeadas',
        price: '₡3,500',
      },
      {
        name: 'Arroz con Pollo',
        description: 'Acompañado de papas tostadas y ensalada',
        price: '₡4,500',
      },
    ],
    reviews: [
      {
        author: 'Roberto A.',
        rating: 4,
        text: 'Muy buena comida, ideal para ir en familia los domingos.',
        date: 'Hace 2 meses',
      },
    ],
    coordinates: { lat: 9.67, lng: -84.01 },
  },
  {
    id: '8',
    name: 'Cafetería El Grano de Oro',
    categoryId: 'cafeterias',
    cantonId: 'tarrazu',
    description: 'Experiencia completa de café, desde el tueste hasta la taza.',
    image: IMAGES[9],
    rating: 4.8,
    reviewCount: 175,
    priceRange: '$$',
    address: 'Barrio La Cruz, San Marcos',
    featured: true,
    tags: ['Tueste', 'Café Tarrazú', 'Brunch'],
    menuItems: [
      {
        name: 'Desayuno Santeño',
        description: 'Pinto, huevo, queso tierno, natilla y plátano',
        price: '₡4,000',
      },
      {
        name: 'Café Chorreado',
        description: 'El método tradicional costarricense',
        price: '₡1,500',
      },
    ],
    reviews: [
      {
        author: 'Elena F.',
        rating: 5,
        text: 'El olor a café tostado cuando entrás es increíble.',
        date: 'Hace 1 semana',
      },
    ],
    coordinates: { lat: 9.659, lng: -84.025 },
  },

  // DOTA
  {
    id: '9',
    name: 'Truchero Don Memo',
    categoryId: 'restaurantes',
    cantonId: 'dota',
    description:
      'Pesca tu propia trucha o disfrutá de las nuestras frescas. Un clásico de Dota.',
    image: IMAGES[2],
    rating: 4.8,
    reviewCount: 420,
    priceRange: '$$',
    address: 'Camino a Providencia, Santa María de Dota',
    featured: true,
    tags: ['Trucha', 'Familiar', 'Naturaleza'],
    menuItems: [
      {
        name: 'Trucha Entera Frita',
        description: 'Acompañada de patacones y ensalada fresca',
        price: '₡7,500',
      },
      {
        name: 'Filet de Trucha al Ajillo',
        description: 'Con puré de papa y vegetales',
        price: '₡8,500',
      },
      {
        name: 'Ceviche de Trucha',
        description: 'Fresco, con limón mandarina de la zona',
        price: '₡4,500',
      },
    ],
    reviews: [
      {
        author: 'Andrés L.',
        rating: 5,
        text: 'La mejor trucha que he comido. El lugar es hermoso, rodeado de naturaleza.',
        date: 'Hace 2 semanas',
      },
      {
        author: 'Valeria M.',
        rating: 5,
        text: 'A los niños les encantó pescar su propia comida.',
        date: 'Hace 1 mes',
      },
    ],
    coordinates: { lat: 9.64, lng: -83.96 },
  },
  {
    id: '10',
    name: 'Soda La Montaña',
    categoryId: 'sodas',
    cantonId: 'dota',
    description:
      'Comida reconfortante para el clima frío de Dota. Porciones generosas.',
    image: IMAGES[8],
    rating: 4.6,
    reviewCount: 185,
    priceRange: '$',
    address: 'Frente al parque, Santa María de Dota',
    featured: false,
    tags: ['Comida Típica', 'Económico', 'Sopas'],
    menuItems: [
      {
        name: 'Sopa de Mondongo',
        description: 'Bien caliente, perfecta para el frío',
        price: '₡4,000',
      },
      {
        name: 'Casado con Pescado',
        description: 'Filet de tilapia con guarniciones',
        price: '₡3,800',
      },
    ],
    reviews: [
      {
        author: 'Jorge C.',
        rating: 4,
        text: 'La sopa de mondongo te revive. Muy buen precio.',
        date: 'Hace 3 días',
      },
    ],
    coordinates: { lat: 9.65, lng: -83.97 },
  },
  {
    id: '11',
    name: 'Restaurante Quetzal',
    categoryId: 'restaurantes',
    cantonId: 'dota',
    description:
      'Gastronomía de altura rodeados del bosque nuboso. Especialidad en carnes y trucha.',
    image: IMAGES[0],
    rating: 4.9,
    reviewCount: 210,
    priceRange: '$$$',
    address: 'San Gerardo de Dota',
    featured: false,
    tags: ['Gourmet', 'Bosque Nuboso', 'Romántico'],
    menuItems: [
      {
        name: 'Lomito Quetzal',
        description: 'Con salsa de moras silvestres',
        price: '₡16,000',
      },
      {
        name: 'Trucha Almendrada',
        description: 'Filet de trucha con costra de almendras',
        price: '₡11,000',
      },
    ],
    reviews: [
      {
        author: 'Patricia R.',
        rating: 5,
        text: 'Un lugar mágico. Vimos quetzales mientras desayunábamos.',
        date: 'Hace 2 meses',
      },
    ],
    coordinates: { lat: 9.55, lng: -83.8 },
  },
  {
    id: '12',
    name: 'Cafetería Dota Estate',
    categoryId: 'cafeterias',
    cantonId: 'dota',
    description:
      'Café de finca, rodeado de cafetales. Ofrecemos tours de café.',
    image: IMAGES[9],
    rating: 4.8,
    reviewCount: 150,
    priceRange: '$$',
    address: 'A las afueras de Santa María',
    featured: false,
    tags: ['Café', 'Tour', 'Naturaleza'],
    menuItems: [
      {
        name: 'Chemex',
        description: 'Café filtrado para compartir, notas cítricas',
        price: '₡4,500',
      },
      {
        name: 'Pan Casero',
        description: 'Recién horneado, con natilla',
        price: '₡2,000',
      },
    ],
    reviews: [
      {
        author: 'Marcos T.',
        rating: 5,
        text: 'El tour de café es súper informativo y el café delicioso.',
        date: 'Hace 1 semana',
      },
    ],
    coordinates: { lat: 9.645, lng: -83.98 },
  },
  {
    id: '13',
    name: 'Soda El Puente',
    categoryId: 'sodas',
    cantonId: 'dota',
    description:
      'Comida casera junto al río. Un lugar tranquilo para almorzar.',
    image: IMAGES[10],
    rating: 4.4,
    reviewCount: 85,
    priceRange: '$',
    address: 'Junto al puente del Río Pirrís, Dota',
    featured: false,
    tags: ['Río', 'Casados', 'Tranquilo'],
    menuItems: [
      {
        name: 'Gallo de Salchichón',
        description: 'Con tortilla palmeada',
        price: '₡1,500',
      },
      {
        name: 'Casado con Pollo en Salsa',
        description: 'El favorito de la casa',
        price: '₡3,500',
      },
    ],
    reviews: [
      {
        author: 'Lucía B.',
        rating: 4,
        text: 'Muy rico y el sonido del río es muy relajante.',
        date: 'Hace 3 semanas',
      },
    ],
    coordinates: { lat: 9.655, lng: -83.965 },
  },
  {
    id: '14',
    name: 'Bar La Cumbre',
    categoryId: 'bares',
    cantonId: 'dota',
    description:
      'El lugar para calentarse en las noches frías de Dota. Cerveza artesanal y buena música.',
    image: IMAGES[11],
    rating: 4.5,
    reviewCount: 110,
    priceRange: '$',
    address: 'Centro de Santa María de Dota',
    featured: false,
    tags: ['Cerveza Artesanal', 'Música en vivo', 'Nocturno'],
    menuItems: [
      {
        name: 'Cerveza Artesanal Local',
        description: 'IPA o Stout',
        price: '₡3,000',
      },
      {
        name: 'Nachos La Cumbre',
        description: 'Con carne mechada y mucho queso',
        price: '₡5,500',
      },
    ],
    reviews: [
      {
        author: 'Kevin D.',
        rating: 5,
        text: 'Excelente ambiente los sábados con música en vivo.',
        date: 'Hace 1 mes',
      },
    ],
    coordinates: { lat: 9.652, lng: -83.971 },
  },

  // LEÓN CORTÉS
  {
    id: '15',
    name: 'Soda Doña María',
    categoryId: 'sodas',
    cantonId: 'leon-cortes',
    description:
      'La soda más tradicional de San Pablo. Famosos por sus tamales los fines de semana.',
    image: IMAGES[10],
    rating: 4.8,
    reviewCount: 210,
    priceRange: '$',
    address: 'Costado norte de la iglesia, San Pablo',
    featured: true,
    tags: ['Tamales', 'Comida Típica', 'Tradicional'],
    menuItems: [
      {
        name: 'Tamal de Cerdo',
        description: 'Receta tradicional, solo sábados y domingos',
        price: '₡1,800',
      },
      {
        name: 'Gallo de Picadillo de Chicasquil',
        description: 'Auténtico sabor local',
        price: '₡1,500',
      },
      {
        name: 'Casado con Carne Asada',
        description: 'Con tortillas recién hechas',
        price: '₡3,500',
      },
    ],
    reviews: [
      {
        author: 'Carmen L.',
        rating: 5,
        text: 'Los tamales de Doña María son los mejores de toda la zona.',
        date: 'Hace 1 semana',
      },
      {
        author: 'José M.',
        rating: 5,
        text: 'Comida con verdadero sabor a hogar.',
        date: 'Hace 2 meses',
      },
    ],
    coordinates: { lat: 9.69, lng: -84.03 },
  },
  {
    id: '16',
    name: 'Restaurante El Campesino',
    categoryId: 'restaurantes',
    cantonId: 'leon-cortes',
    description:
      'De la granja a la mesa. Utilizamos vegetales orgánicos cultivados en nuestra propiedad.',
    image: IMAGES[7],
    rating: 4.7,
    reviewCount: 145,
    priceRange: '$$',
    address: 'Camino a San Andrés, León Cortés',
    featured: false,
    tags: ['Orgánico', 'Granja a la mesa', 'Rústico'],
    menuItems: [
      {
        name: 'Ensalada de la Huerta',
        description: 'Vegetales frescos del día con aderezo de la casa',
        price: '₡3,500',
      },
      {
        name: 'Pollo Campestre',
        description: 'Pollo criado libre, asado a la leña',
        price: '₡6,500',
      },
    ],
    reviews: [
      {
        author: 'Silvia P.',
        rating: 4,
        text: 'Se nota la frescura de los ingredientes. Muy recomendado.',
        date: 'Hace 3 semanas',
      },
    ],
    coordinates: { lat: 9.7, lng: -84.04 },
  },
  {
    id: '17',
    name: 'Ventanita El Cruce',
    categoryId: 'ventanitas',
    cantonId: 'leon-cortes',
    description:
      'Parada obligatoria en carretera. Los mejores chicharrones al paso.',
    image: IMAGES[4],
    rating: 4.5,
    reviewCount: 320,
    priceRange: '$',
    address: 'Cruce principal de San Pablo',
    featured: false,
    tags: ['Chicharrones', 'Rápido', 'Carretera'],
    menuItems: [
      {
        name: 'Cono de Chicharrones',
        description: 'Chicharrón de carne con yuca frita',
        price: '₡3,500',
      },
      {
        name: 'Empanada de Queso',
        description: 'Recién frita',
        price: '₡1,000',
      },
    ],
    reviews: [
      {
        author: 'Fabián Q.',
        rating: 5,
        text: 'Siempre paro aquí cuando voy para San José. Chicharrones buenísimos.',
        date: 'Hace 4 días',
      },
    ],
    coordinates: { lat: 9.688, lng: -84.025 },
  },
  {
    id: '18',
    name: 'Cafetería San Pablo',
    categoryId: 'cafeterias',
    cantonId: 'leon-cortes',
    description:
      'Un rincón acogedor en el centro del pueblo. Café local y repostería casera.',
    image: IMAGES[9],
    rating: 4.6,
    reviewCount: 112,
    priceRange: '$$',
    address: 'Frente al parque, San Pablo',
    featured: false,
    tags: ['Café', 'Repostería', 'Acogedor'],
    menuItems: [
      {
        name: 'Café con Leche',
        description: 'Café de León Cortés',
        price: '₡1,800',
      },
      {
        name: 'Queque Seco',
        description: 'Perfecto para acompañar el café',
        price: '₡1,500',
      },
    ],
    reviews: [
      {
        author: 'Marta V.',
        rating: 4,
        text: 'Lugar muy tranquilo para tomar café por la tarde.',
        date: 'Hace 2 semanas',
      },
    ],
    coordinates: { lat: 9.691, lng: -84.031 },
  },
  {
    id: '19',
    name: 'Restaurante Las Piedras',
    categoryId: 'restaurantes',
    cantonId: 'leon-cortes',
    description:
      'Carnes a la parrilla con una hermosa vista a las montañas de León Cortés.',
    image: IMAGES[1],
    rating: 4.5,
    reviewCount: 160,
    priceRange: '$$',
    address: 'Carretera a Llano Bonito',
    featured: false,
    tags: ['Parrilla', 'Vistas', 'Familiar'],
    menuItems: [
      {
        name: 'Churrasco',
        description: 'Corte de 300g con chimichurri',
        price: '₡11,000',
      },
      {
        name: 'Pechuga a la Plancha',
        description: 'Con ensalada y puré',
        price: '₡5,500',
      },
    ],
    reviews: [
      {
        author: 'Daniela S.',
        rating: 5,
        text: 'La carne en su punto y la vista es preciosa.',
        date: 'Hace 1 mes',
      },
    ],
    coordinates: { lat: 9.68, lng: -84.05 },
  },
]
